import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import React, { useState } from "react";
import { formatMoney, ToDineroObj } from "src/Utils/MoneyUtils";

// Function to get stakeholder color
const getStakeholderColor = (stakeholder: string): string => {
  const stakeholderType = stakeholder.toLowerCase();

  if (stakeholderType.includes("vendor")) {
    return "#ffcc80"; // Orange
  } else if (stakeholderType.includes("finance")) {
    return "#ffab91"; // Coral
  } else if (stakeholderType.includes("warehouse")) {
    return "#81d4fa"; // Blue
  } else if (stakeholderType.includes("procurement")) {
    return "#a5d6a7"; // Green
  }

  return "#e0e0e0"; // Default grey
};

interface SummaryData {
  stakeholder: string;
  Total: any;
  "0-7 Days": any;
  "8-15 Days": any;
  "16-30 Days": any;
  "31-45 Days": any;
  "45-90 Days": any;
  ">90 Days": any;
  expanded?: boolean;
  subRows?: SummaryData[];
}

interface DetailedSummariesTableProps {
  title?: string;
  data?: SummaryData[];
}

interface TableRowProps {
  children: React.ReactNode;
  isSubRow?: boolean;
  color: string;
  [key: string]: any;
}

interface TableCellProps {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
  width?: string;
  isHeader?: boolean;
  isSubRow?: boolean;
  [key: string]: any;
}

const DetailedSummariesTable: React.FC<DetailedSummariesTableProps> = ({
  title = "Overall Disputed Summary",
  data: propData,
}) => {
  const [data, setData] = useState<SummaryData[]>(propData);

  const toggleRow = (index: number) => {
    setData((prevData) => prevData.map((row, i) => (i === index ? { ...row, expanded: !row.expanded } : row)));
  };

  const TableRow: React.FC<TableRowProps> = ({ children, isSubRow = false, color, ...props }) => (
    <Box
      sx={{
        display: "flex",
        minHeight: "51px",
        alignItems: "center",
        borderBottom: "1px solid #e0e0e0",
        position: "relative",
        backgroundColor: isSubRow ? "#ffffff" : "transparent",
        "&:before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          width: "8px",
          height: "100%",
          backgroundColor: color,
        },
      }}
      {...props}
    >
      {children}
    </Box>
  );

  const TableCell: React.FC<TableCellProps> = ({
    children,
    align = "left",
    width,
    isHeader = false,
    isSubRow = false,
    ...props
  }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: align === "center" ? "center" : "flex-start",
        padding: isSubRow ? "0 16px 0 60px" : isHeader ? "0 16px" : "0 22px",
        width: width || "auto",
        flex: width ? "none" : 1,
        fontFamily: "Inter",
        fontWeight: isHeader ? 600 : 400,
        fontSize: "12px",
        color: "rgba(0,0,0,0.87)",
        letterSpacing: isHeader ? "0.012px" : "0.1176px",
        minHeight: "51px",
      }}
      {...props}
    >
      {children}
    </Box>
  );

  return (
    <Paper
      sx={{
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        boxShadow:
          "0px 1px 3px 0px rgba(0,0,0,0.12), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.2)",
        overflow: "hidden",
        margin: "16px 0",
      }}
    >
      <Box sx={{ padding: "16px" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 600,
              fontSize: "16px",
              color: "rgba(0,0,0,0.87)",
              letterSpacing: "0.024px",
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Table */}

        <Box
          sx={{
            border: "1px solid rgba(0,0,0,0.01)",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {/* Table Header */}
          <Box
            sx={{
              display: "flex",
              height: "51px",
              backgroundColor: "#fafafa",
              borderBottom: "1px solid #e0e0e0",
              alignItems: "center",
            }}
          >
            <TableCell width="200px" isHeader>
              Stakeholders
            </TableCell>
            <TableCell align="center" isHeader>
              Total
            </TableCell>
            <TableCell align="center" isHeader>
              0-7 Days
            </TableCell>
            <TableCell align="center" isHeader>
              8-15 Days
            </TableCell>
            <TableCell align="center" isHeader>
              16-30 Days
            </TableCell>
            <TableCell align="center" isHeader>
              31-45 Days
            </TableCell>
            <TableCell align="center" isHeader>
              45-90 Days
            </TableCell>
            <TableCell align="center" isHeader>
              &gt;90 Days
            </TableCell>
          </Box>

          {/* Table Body */}
          {data.map((row, index) => (
            <React.Fragment key={index}>
              <TableRow color={getStakeholderColor(row.stakeholder)}>
                <TableCell width="200px">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      minHeight: "51px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "rgba(0,0,0,0.87)",
                        letterSpacing: "0.012px",
                        flex: 1,
                        marginRight: "8px",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        lineHeight: "1.3",
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                      title={row.stakeholder} // Show full text on hover
                    >
                      {row.stakeholder}
                    </Typography>
                    {row.subRows && row.subRows.length > 0 && (
                      <IconButton
                        onClick={() => toggleRow(index)}
                        sx={{
                          padding: "5px",
                          borderRadius: "100px",
                          marginRight: "8px",
                          minWidth: "24px",
                          width: "24px",
                          height: "24px",
                          flexShrink: 0,
                        }}
                      >
                        {row.expanded ? (
                          <KeyboardArrowDownIcon sx={{ fontSize: "16px", color: "#666" }} />
                        ) : (
                          <KeyboardArrowRightIcon sx={{ fontSize: "16px", color: "#666" }} />
                        )}
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">{formatMoney(ToDineroObj(row.Total))}</TableCell>
                <TableCell align="center">{formatMoney(ToDineroObj(row["0-7 Days"]))}</TableCell>
                <TableCell align="center">{formatMoney(ToDineroObj(row["8-15 Days"]))}</TableCell>
                <TableCell align="center">{formatMoney(ToDineroObj(row["16-30 Days"]))}</TableCell>
                <TableCell align="center">{formatMoney(ToDineroObj(row["31-45 Days"]))}</TableCell>
                <TableCell align="center">{formatMoney(ToDineroObj(row["45-90 Days"]))}</TableCell>
                <TableCell align="center">{formatMoney(ToDineroObj(row[">90 Days"]))}</TableCell>
              </TableRow>

              {/* Sub-rows */}
              {row.expanded &&
                row.subRows &&
                row.subRows.map((subRow, subIndex) => (
                  <TableRow key={`${index}-${subIndex}`} isSubRow color={getStakeholderColor(row.stakeholder)}>
                    <TableCell width="200px" isSubRow>
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 400,
                          fontSize: "12px",
                          color: "rgba(0,0,0,0.87)",
                          letterSpacing: "0.1176px",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                          lineHeight: "1.3",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }}
                        title={subRow.stakeholder} // Show full text on hover
                      >
                        {subRow.stakeholder}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" isSubRow>
                      {formatMoney(ToDineroObj(subRow.Total))}
                    </TableCell>
                    <TableCell align="center" isSubRow>
                      {formatMoney(ToDineroObj(subRow["0-7 Days"]))}
                    </TableCell>
                    <TableCell align="center" isSubRow>
                      {formatMoney(ToDineroObj(subRow["8-15 Days"]))}
                    </TableCell>
                    <TableCell align="center" isSubRow>
                      {formatMoney(ToDineroObj(subRow["16-30 Days"]))}
                    </TableCell>
                    <TableCell align="center" isSubRow>
                      {formatMoney(ToDineroObj(subRow["31-45 Days"]))}
                    </TableCell>
                    <TableCell align="center" isSubRow>
                      {formatMoney(ToDineroObj(subRow["45-90 Days"]))}
                    </TableCell>
                    <TableCell align="center" isSubRow>
                      {formatMoney(ToDineroObj(subRow[">90 Days"]))}
                    </TableCell>
                  </TableRow>
                ))}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default DetailedSummariesTable;
